import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import Input from '../../components/Input'
import Keyboard from '../../components/Keyboard'
import { KeyBoardDistributionEnum } from '../../components/Keyboard/types'
import LogoHeader from '../../components/LogoHeader/LogoHeader'
import Spinner from '../../components/Spinner/SpinnerTS'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import googleAnalytics4 from '../../lib/googleAnalytics'
import tts from '../../lib/TTS'
import ChangeStationCarousel from './ChangeStationCarousel'
import ChangeStationHeader from './ChangeStationHeader'
import { useChangeStation } from './hooks/useChangeStation'
import { ChangeStationHeaderRef, SearchErrorEnum, StationData } from './types'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import { LocalizeStationStep } from '../../reducers/types/reducerTypes'
import { ERROR_STATUS } from '../ErrorScreen/types'

export default function ChangeStationScreen() {
  const navigate = useNavigate()
  const changeStationHeaderRef = useRef<ChangeStationHeaderRef>(null)
  const {
    isLoading,
    isSearching,
    stationsList,
    hasStations,
    errorOnSearch,
    getInitialStations,
    searchByZipCode,
    setUserStation,
  } = useChangeStation()
  const { localize_station_step } = useAppSelector((state) => state.station)
  const dispatch = useAppDispatch()

  const { focusKey } = useHybridFocusable({
    focusKey: 'CHANGE_STATION_SCREEN',
  })

  // Workaround to allow new focus components to work with spatial navigation and old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  useEffect(() => {
    googleAnalytics4.sendPageViewEvent('Change Station')
    getInitialStations()
    tts.pushTextToRead(
      'You can change your station on this page. Enter 5-digit zip code to find your local stations'
    )
  }, [])

  useKeyDown(KeyTypeEnum.Back, () => {
    handleNavigation()
  })

  const handleNavigation = () => {
    if (localize_station_step === LocalizeStationStep.RELOCALIZING) {
      onLocalizationCompleteNavigate()
    } else {
      navigate(-1)
    }
  }

  const onLocalizationCompleteNavigate = () => {
    dispatch({
      type: 'UPDATE_LOCALIZATION_STEP',
      payload: LocalizeStationStep.COMPLETED,
    })
    navigate('/home')
  }

  // Handle numeric keys from remote control
  useKeyDown(
    [
      KeyTypeEnum.Number0,
      KeyTypeEnum.Number1,
      KeyTypeEnum.Number2,
      KeyTypeEnum.Number3,
      KeyTypeEnum.Number4,
      KeyTypeEnum.Number5,
      KeyTypeEnum.Number6,
      KeyTypeEnum.Number7,
      KeyTypeEnum.Number8,
      KeyTypeEnum.Number9,
    ],
    (key) => {
      if (!key) return
      // The key value is already the digit ('0'-'9')
      tts.addTextToRead(`Entered ${key}`)
      changeStationHeaderRef.current?.addDigit(key)
    }
  )

  useEffect(() => {
    if (errorOnSearch === null || hasStations) return

    if (
      [SearchErrorEnum.NOT_FOUND, SearchErrorEnum.GENERIC].includes(
        errorOnSearch
      )
    ) {
      tts.addTextToRead(
        'There are no stations available for your selected ZIP Code.'
      )
    }
  }, [errorOnSearch])

  const onStationSelected = async (station: StationData) => {
    tts.stopRead()
    const result = await setUserStation(station)

    if (result) {
      handleNavigation()
    } else {
      dispatch({
        type: 'TRIGGER_ERROR',
        payload: {
          status: ERROR_STATUS.UNAVAILABLE_STATION,
          content: {
            errorPath: location.pathname,
          },
          codeBaseLocation: 'ChangeStationScreen - onStationSelected',
        },
      })
      navigate('/error')
    }
  }

  return !isLoading ? (
    <div className="change-station-container">
      <LogoHeader />
      <FocusContext.Provider value={focusKey}>
        <ChangeStationHeader
          ref={changeStationHeaderRef}
          handleSearchCleared={() => {
            getInitialStations(false)
          }}
          onZipCodeEntered={(zipCode: string) => {
            searchByZipCode(zipCode)
          }}
        >
          {(searchQuery, onEnterPress) => (
            <>
              <Input
                searchQuery={searchQuery}
                showCursor={false}
                showSearchIcon={false}
                placeholder="Enter 5-Digit ZIP Code"
              />
              <Keyboard
                onEnterPress={onEnterPress}
                keyBoardDistribution={KeyBoardDistributionEnum.numeric}
                allowSpaces={false}
                stealFocus
              />
            </>
          )}
        </ChangeStationHeader>

        <div className="near-you">STATIONS NEAR YOU:</div>
        {isSearching && (
          <div className="spinner-container">
            <Spinner />
          </div>
        )}
        {!isSearching && hasStations && (
          <ChangeStationCarousel
            collection={stationsList}
            onStationSelected={onStationSelected}
          />
        )}
      </FocusContext.Provider>
      {!isSearching &&
        !hasStations &&
        errorOnSearch === SearchErrorEnum.NOT_FOUND && (
          <p className="station-error">
            There are no stations available for your selected ZIP Code.
          </p>
        )}
      {!isSearching &&
        !hasStations &&
        errorOnSearch === SearchErrorEnum.GENERIC && (
          <p className="station-error">
            We weren&#39;t able to look up your ZIP Code at this time. Please
            try again later.
          </p>
        )}
    </div>
  ) : (
    <Spinner />
  )
}
